import { Table, Breadcrumb, Button, Modal, message, Tooltip, Space } from "antd";
import React, { useEffect, useState } from "react";
import { JobType } from "../../../constants/JobTypes";
import { JobConfig, JobRun } from "../../../models/interfaces";
import { useDispatch } from "react-redux";
import mixpanel from "mixpanel-browser";
import type { AppDispatch } from "../../../redux/store";
import {
  fetchJobConfigList,
  fetchJobRunList,
} from "../../../redux/geoLiftSlice";
import {
  clearSession,
  setSessionFetched,
  setSessionLoading,
  resetForStudySwitch,
} from "../../../redux/testDesignSlice";
import { useSelector } from "react-redux";
import type { RootState } from "../../../redux/store";
import { Link, useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import Loader from "../../../components/Loader";
import { Events } from "../../../constants/Event";
import { PlusCircleOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import { RefreshCcw } from "lucide-react";
import axiosInstance from "../../../utils/axiosInstance";
import DownloadReportButton from "./components/DownloadReportButton";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const geoBreadCrumbs = [
  {
    name: "Home",
    url: "/",
  },
  {
    name: "Measurement",
    url: "/measurement/geo-lift",
  },
  {
    name: "Geo Lift Measurement",
    url: "/measurement/synthetic_control_list",
  },
];

const GeoLift: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState<boolean>(false);
  const [deletingJobs, setDeletingJobs] = useState<{ [key: string]: boolean }>({});

  // Access state from Redux
  const jobConfigList = useSelector(
    (state: RootState) => state.geoLift.jobConfigList
  );
  const jobRunList = useSelector(
    (state: RootState) => state.geoLift.jobRunList
  );
  const totalCount = useSelector(
    (state: RootState) => state.geoLift.totalCount
  );
  // Fetch data function
  const fetchData = async (page: number = 1, limit: number = 20) => {
    setLoading(true);
    try {
      const offset = (page - 1) * limit;
      await Promise.all([
        dispatch(
          fetchJobConfigList({
            limit,
            offset,
          })
        ),
        dispatch(
          fetchJobRunList({
            limit,
            offset,
          })
        ),
      ]);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchData(currentPage, pageSize);
  }, [dispatch]);
  const handlePageChange = (page: number, size?: number) => {
    setCurrentPage(page);
    if (size && size !== pageSize) {
      setPageSize(size);
    }
    fetchData(page, size || pageSize);
  };
  const handlePageSizeChange = (current: number, size: number) => {
    setCurrentPage(1); // Reset to first page when changing page size
    setPageSize(size);
    fetchData(1, size);
  };

  const handleViewClick = (
    jobType: JobType,
    jobConfigId: string,
    jobName: string,
    buyType: string,
    recommendationStatus?: string,
    modelParameterConfig?: {},
  ) => {
    if (jobType === JobType.GeoLift) {
      let url = `/measurement/geo-lift/${jobConfigId}?name=${encodeURIComponent(
        jobName
      )}`;
      if (recommendationStatus) {
        url += `&recStatus=${encodeURIComponent(recommendationStatus)}`;
      }
      dispatch(resetForStudySwitch());
      navigate(url, {
        state: {
          jobType,
          jobConfigId,
          jobName,
          buyType,
          modelParameterConfig,
        },
      });
    }
    mixpanel.track(Events.BUTTON_CLICK, {
      jobType,
      action: "View",
      jobConfigId,
    });
  };

  const handleEditClick = async (jobConfigId: string, jobName: string) => {
    try {
      // Fetch job config to get buyType
      const response = await axiosInstance.get(`/api/jobConfig/${jobConfigId}`);
      const buyType = response.data?.modelParameterConfig?.parameters?.buyType || "non-national";

      // Navigate to the geo-lift page with edit mode and buyType
      // Set mode based on buyType: national = evaluate, non-national = plan
      const mode = buyType === "national" ? "evaluate" : "plan";
      navigate(`/measurement/geo-lift/${jobConfigId}?name=${encodeURIComponent(jobName)}&edit=true&mode=${mode}`, {
        state: {
          buyType: buyType,
        }
      });

      mixpanel.track(Events.BUTTON_CLICK, {
        jobType: JobType.GeoLift,
        action: "Edit",
        jobConfigId,
      });
    } catch (error) {
      console.error("Error fetching job config:", error);
      // Fallback to default navigation
      navigate(`/measurement/geo-lift/${jobConfigId}?name=${encodeURIComponent(jobName)}&edit=true`);
    }
  };

  const handleDeleteClick = async (jobConfigId: string, jobName: string) => {
    Modal.confirm({
      title: "Delete Geo Lift Study",
      content: `Are you sure you want to delete "${jobName}"? This action cannot be undone.`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          setDeletingJobs((prev) => ({ ...prev, [jobConfigId]: true }));

          await axiosInstance.delete(`/api/jobConfig/${jobConfigId}`, {
            headers: {
              Authorization: "Bearer " + localStorage.getItem("authToken"),
            },
          });

          mixpanel.track(Events.BUTTON_CLICK, {
            jobType: JobType.GeoLift,
            action: "Delete",
            jobConfigId,
          });

          message.success(`Successfully deleted "${jobName}"`);

          // Hard refresh the page to reload the job list
          setTimeout(() => {
            window.location.reload();
          }, 500);
        } catch (error: any) {
          console.error("Error deleting job:", error);
          message.error(
            error?.response?.data?.message ||
            "Failed to delete job configuration. Please try again."
          );
        } finally {
          setDeletingJobs((prev) => ({ ...prev, [jobConfigId]: false }));
        }
      },
    });
  };

  const getJobStatus = (jobConfig: JobConfig, jobRunList: JobRun[]): string => {
    const run = jobRunList.find(
      (jobRun) => jobRun.jobConfigId === jobConfig.jobConfigId
    );

    if (run) {
      const { jobStatus, jobExecutionTime } = run;
      const isStuck =
        (jobStatus === "RUNNING" || jobStatus === "PENDING") &&
        dayjs.utc().diff(dayjs.utc(jobExecutionTime), "hour") >= 2;
      return isStuck ? "FAILED" : jobStatus;
    } else {
      // No matching run found — check if *any* run for this config exists and is stale
      const staleRun = jobRunList.find(
        (jobRun) =>
          jobRun.jobConfigId === jobConfig.jobConfigId &&
          dayjs().diff(dayjs(jobRun.jobExecutionTime), "hour") >= 2
      );

      return staleRun ? "FAILED" : "UNKNOWN";
    }
  };

  const records = [...jobConfigList]
    .sort(
      (a, b) =>
        new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
    )
    .map((jobConfig: any) => ({
      key: jobConfig.jobConfigId, // Ensure unique row keys
      name: jobConfig.name,
      createdDate: jobConfig.createdDate,
      status: getJobStatus(jobConfig, jobRunList),
      recommendationStatus: jobConfig.status,
      jobType: jobConfig.jobType,
      jobConfigId: jobConfig.jobConfigId, // Needed for links
      jobExecutionTime: jobConfig.jobExecutionTime,
      buyType: jobConfig?.modelParameterConfig?.parameters?.buyType || "",
      modelParameterConfig: jobConfig.modelParameterConfig,
    }));

  if (loading) return <Loader />;

  return (
    <div className="h-full overflow-y-auto p-5">
      <div className="mb-6 flex justify-between items-center">
        <Breadcrumb className="">
          {geoBreadCrumbs.map((entity: any, index: number) => (
            <Breadcrumb.Item key={index}>
              <Link to={entity.url}>{entity.name}</Link>
            </Breadcrumb.Item>
          ))}
        </Breadcrumb>
        <div className="flex items-center gap-2 justify-end">
          <Button
            type="primary"
            icon={<PlusCircleOutlined className="h-4 w-4" />}
            className="flex items-center gap-2"
            onClick={() => {
              dispatch(clearSession());
              dispatch(setSessionFetched(false));
              dispatch(setSessionLoading(false));
              navigate(`/measurement/geo-lift/${uuidv4()}?mode=evaluate`);
            }}
          >
            Run Synthetic Control Test
          </Button>
          <Button
            type="primary"
            icon={<RefreshCcw className="h-4 w-4" />}
            onClick={() => fetchData(currentPage, pageSize)}
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Job Config Table */}
      <Table
        columns={[
          {
            title: "Name",
            dataIndex: "name",
            key: "name",
            sorter: (a, b) => a.name.localeCompare(b.name),
            render: (text: string) => (
              <span className="text-blue-600 hover:text-blue-800">{text}</span>
            ),
          },
          {
            title: "Created Date",
            dataIndex: "createdDate",
            key: "createdDate",

            render: (date) =>
              dayjs.utc(`${date}Z`).local().format("MMM D, YYYY HH:mm"),

            sorter: (a, b) =>
              dayjs.utc(`${a.createdDate}Z`).unix() -
              dayjs.utc(`${b.createdDate}Z`).unix(),
          },
          {
            title: "Action",
            dataIndex: "status",
            key: "action",
            filters: [
              { text: "Running", value: "RUNNING" },
              { text: "In Queue", value: "PENDING" },
              { text: "Failed", value: "FAILED" },
              { text: "Successful", value: "SUCCESSFUL" },
              { text: "Not started", value: "UNKNOWN" },
            ],
            onFilter: (value, record) => record.status === value,
            render: (status, record) => {
              const isDeleting = deletingJobs[record.jobConfigId];
              switch (status) {
                case "RUNNING":
                  return (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent"></div>
                      <span>Running</span>
                    </div>
                  );
                case "PENDING":
                  return (
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1">
                        <div className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                        <div className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                        <div className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce"></div>
                      </div>
                      <span>In Queue</span>
                    </div>
                  );
                case "FAILED":
                  return (
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="flex items-center gap-2 text-red-600">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                        <span>Failed</span>
                      </div>
                      <Tooltip title="Edit job configuration">
                        <Button
                          type="link"
                          size="small"
                          icon={<EditOutlined />}
                          onClick={() =>
                            handleEditClick(record.jobConfigId, record.name)
                          }
                          disabled={deletingJobs[record.jobConfigId]}
                        >
                          Edit
                        </Button>
                      </Tooltip>
                      <Tooltip title="Delete job configuration">
                        <Button
                          type="link"
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={() =>
                            handleDeleteClick(record.jobConfigId, record.name)
                          }
                          loading={deletingJobs[record.jobConfigId]}
                          disabled={deletingJobs[record.jobConfigId]}
                        >
                          Delete
                        </Button>
                      </Tooltip>
                    </div>
                  );
                case "SUCCESSFUL":
                  return (
                    <div className="flex items-center gap-2 flex-nowrap">
                      <Button
                        type="primary"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() =>
                          handleViewClick(
                            record.jobType,
                            record.jobConfigId,
                            record.name,
                            record?.buyType,
                            undefined,
                            record?.modelParameterConfig,
                          )
                        }
                      >
                        View
                      </Button>
                      {record?.recommendationStatus ===
                        "recommendation_success" &&
                        record.buyType === "non-national" && (
                          <Button
                            type="primary"
                            size="small"
                            onClick={() =>
                              handleViewClick(
                                record.jobType,
                                record.jobConfigId,
                                record.name,
                                record?.buyType,
                                record?.recommendationStatus,
                                record?.modelParameterConfig,
                              )
                            }
                          >
                            View Recommendation
                          </Button>
                        )}
                      {/* Only show download button once markets are processed */}
                        {record?.buyType === "national" && record?.jobConfigId && (
                          <div style={{ position: "relative", lineHeight: 0 }}>
                            <DownloadReportButton
                              job_config_id={record.jobConfigId}
                              buttonProps={{ type: "primary", size: "small" }}
                            />
                          </div>
                      )}
                      <Tooltip title="Edit study configuration">
                        <Button
                          type="link"
                          size="small"
                          icon={<EditOutlined />}
                          onClick={() => handleEditClick(record.jobConfigId, record.name)}
                          disabled={isDeleting}
                        >
                          Edit
                        </Button>
                      </Tooltip>
                      <Tooltip title="Delete study">
                        <Button
                          type="link"
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={() => handleDeleteClick(record.jobConfigId, record.name)}
                          loading={isDeleting}
                          disabled={isDeleting}
                        >
                          Delete
                        </Button>
                      </Tooltip>
                    </div>
                  );
                default:
                  return (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-gray-400 text-sm">Not started</span>
                      <Tooltip title="Delete study">
                        <Button
                          type="link"
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={() =>
                            handleDeleteClick(record.jobConfigId, record.name)
                          }
                          loading={deletingJobs[record.jobConfigId]}
                          disabled={deletingJobs[record.jobConfigId]}
                        >
                          Delete
                        </Button>
                      </Tooltip>
                    </div>
                  );
              }
            },
          },
        ]}
        dataSource={records}
        rowKey="key"
        scroll={{ y: "calc(100vh - 280px)" }} // Adjusted to account for pagination
        pagination={{
          pageSize: pageSize,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
          pageSizeOptions: ["10", "20", "50", "100"],
          onShowSizeChange: (_current, size) => setPageSize(size),
          position: ["bottomRight"],
          className: "mt-4",
        }}
      />
    </div>
  );
};

export default GeoLift;
